import { useEffect, useRef, useState } from "react";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import {
  auth,
  getFirebaseAuthDebugSnapshot,
  getFirebaseObserverSubscriberCount,
  isAuthLoading,
  subScribeToAuthStateChange,
} from "../../../lib/firebase";
import type { AuthError, AuthUser } from "../types/auth.types";

const logger = FrontendLoggerFactory.getLogger().withComponent("useAuthState");

const resolveRoutePath = () =>
  typeof window === "undefined" ? null : window.location.pathname;

const resolveOrigin = () =>
  typeof window === "undefined" ? null : window.location.origin;

const getProviderIds = (user: AuthUser | null): string[] => {
  if (!user || user.providerData.length === 0) {
    return [];
  }

  return user.providerData.map((entry) => entry.providerId);
};

/**
 * Hook that mirrors the shared app-level Firebase auth signal.
 *
 * @remarks
 * Firebase recommends a single auth-state observer per app surface. We already keep that
 * observer in `src/lib/firebase.ts` for store/logging sync, so this hook consumes the same
 * signal instead of attaching a second independent observer that can lag behind route guards.
 *
 * This keeps login redirects, claim checks, and the global auth logger on the same user/loading
 * state during initial session restore and localhost custom-token handoffs.
 */
export const useAuthState = () => {
  const [user, setUser] = useState<AuthUser | null>(() => auth.currentUser);
  const [loading, setLoading] = useState(() => isAuthLoading());
  const mountedAtMsRef = useRef(Date.now());
  const lastUserPresentRef = useRef(Boolean(auth.currentUser));
  const lastLoadingRef = useRef(isAuthLoading());

  useEffect(() => {
    try {
      const unsubscribe = subScribeToAuthStateChange((firebaseUser) => {
        const hadUserBefore = lastUserPresentRef.current;
        const loadingBefore = lastLoadingRef.current;
        const hasUserAfter = Boolean(firebaseUser);

        setUser(firebaseUser);
        setLoading(false);
        lastUserPresentRef.current = hasUserAfter;
        lastLoadingRef.current = false;

        logger.debug("useAuthState received shared auth update", {
          operation: "auth_state_changed",
          hadUserBefore,
          hasUserAfter,
          userIdPresent: hasUserAfter,
          loadingBefore,
          loadingAfter: false,
          providerIds: getProviderIds(firebaseUser),
          elapsedMsSinceMount: Date.now() - mountedAtMsRef.current,
          routePath: resolveRoutePath(),
          origin: resolveOrigin(),
        });
      });

      logger.debug("useAuthState subscribed to shared auth bridge", {
        operation: "auth_state_subscription_added",
        userPresent: Boolean(auth.currentUser),
        loading: lastLoadingRef.current,
        isAuthLoading: isAuthLoading(),
        routePath: resolveRoutePath(),
        origin: resolveOrigin(),
        subscriberCountAfterAdd: getFirebaseObserverSubscriberCount(),
      });

      // If the shared observer already resolved before this hook mounted, sync immediately
      // from `auth.currentUser` instead of waiting for another auth event.
      if (!isAuthLoading()) {
        const userPresent = Boolean(auth.currentUser);
        setUser(auth.currentUser);
        setLoading(false);
        lastUserPresentRef.current = userPresent;
        lastLoadingRef.current = false;

        logger.debug("useAuthState applied immediate auth sync", {
          operation: "auth_state_initial_sync_applied",
          userPresent,
          isAuthLoading: false,
          routePath: resolveRoutePath(),
          origin: resolveOrigin(),
          subscriberCount: getFirebaseObserverSubscriberCount(),
          authDomain: getFirebaseAuthDebugSnapshot().authDomain,
        });
      }

      return unsubscribe;
    } catch (error) {
      logger.error("Firebase Auth not initialized:", error as Error, {
        operation: "auth_state_subscription_failed",
        routePath: resolveRoutePath(),
        origin: resolveOrigin(),
      });
      // Defer the fallback reset so a failed observer setup does not
      // synchronously update state while React is unwinding this effect.
      void Promise.resolve().then(() => {
        setLoading(false);
        setUser(null);
        lastUserPresentRef.current = false;
        lastLoadingRef.current = false;
      });
      return undefined;
    }
  }, []);

  return {
    user,
    loading,
    isAuthenticated: !!user,
    error: null as AuthError | null,
  };
};

export default useAuthState;
